import React, { useState } from 'react';
import { Check, ArrowRight, Star, Shield, Clock, Zap } from 'lucide-react';
import AppLink from './AppLink';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import SEO from './SEO';
import { useLocation } from 'react-router-dom';
import { redirectToCheckout, type MolliePlanId } from '../lib/paymentService';
import { useAuth } from '../hooks/useAuth';
import { AnimatePresence } from 'motion/react';
import AuthModal from './AuthModal';

const FAQ_ITEMS = [
  {
    q: 'Ces accès sont-ils des abonnements ?',
    a: "Non. Chaque offre est un paiement unique, sans renouvellement automatique. Vous payez une fois et vous accédez au contenu pendant la durée indiquée.",
  },
  {
    q: "Quelle différence entre Intégration et Praticien ?",
    a: "Les deux donnent un accès de 6 mois. Le Praticien inclut en plus les protocoles avancés complets, l'accompagnement communauté et l'accès bêta aux nouvelles fonctions.",
  },
  {
    q: 'Mes données sont-elles protégées ?',
    a: "Vos données de régulation nerveuse sont chiffrées et ne sont jamais revendues. Vous pouvez demander leur suppression à tout moment depuis les paramètres.",
  },
  {
    q: 'Puis-je commencer gratuitement ?',
    a: "Oui. L'évaluation de base est gratuite, sans inscription et sans carte bancaire. Vous passez à une offre payante quand vous êtes prêt.",
  },
];

export default function PricingPage() {
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [paymentLoading, setPaymentLoading] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showAuth, setShowAuth] = useState(false);

  const { isAuthenticated } = useAuth();

  const handleCheckout = async (planId: MolliePlanId) => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    setPaymentLoading(planId);
    setPaymentError(null);
    try {
      await redirectToCheckout(planId);
    } catch (err) {
      console.error('[PricingPage] Erreur checkout:', err);
      setPaymentError('Une erreur est survenue. Merci de réessayer ou de contacter le support.');
      setPaymentLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">
      <SEO
        title="Tarifs — Amargi"
        description="Ancrage 19 €, Intégration 69 €, Praticien 89 € — paiements uniques, sans abonnement."
        path={location.pathname}
      >
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Amargi — Bilan du système nerveux",
          "description": "Outil de régulation somatique guidé par l'IA, basé sur la théorie polyvagale.",
          "brand": { "@type": "Brand", "name": "Amargi" },
          "offers": [
            {
              "@type": "Offer",
              "name": "Ancrage",
              "price": "19",
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Intégration",
              "price": "69",
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Praticien",
              "price": "89",
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock"
            }
          ]
        })}</script>
      </SEO>
      <SiteNav logoVariant="primary" />
      <AnimatePresence>
        {showAuth && (
          <AuthModal
            onClose={() => setShowAuth(false)}
            title="Créer un compte"
            subtitle="Connectez-vous pour accéder à votre espace et finaliser votre achat."
          />
        )}
      </AnimatePresence>

      {/* ── Hero ── */}
      <section className="pt-[120px] pb-12 px-5 md:px-8 text-center max-w-[760px] mx-auto">
        <span className="inline-block text-[11px] font-bold uppercase tracking-[0.12em] text-amber mb-4">
          Tarifs
        </span>
        <h1 className="text-[36px] md:text-[52px] font-serif font-semibold text-ink leading-[1.1] tracking-[-0.02em] mb-5">
          Le changement a{' '}
          <em className="not-italic text-amber">une profondeur.</em>
        </h1>
        <p className="text-[16px] md:text-[18px] text-taupe leading-relaxed max-w-[520px] mx-auto">
          Commence gratuitement. Choisissez votre accès quand vous êtes prêt.
        </p>
      </section>

      {/* ── Erreur checkout ── */}
      {paymentError && (
        <div className="px-5 md:px-8 pb-4 max-w-[760px] mx-auto">
          <p className="text-[13px] text-crise bg-crise/8 border border-crise/20 rounded-xl px-4 py-3 text-center">
            {paymentError}
          </p>
        </div>
      )}

      {/* ── 3 cartes côte à côte ── */}
      <section className="px-5 md:px-8 pb-10 max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">

          {/* ── Carte Ancrage ── */}
          <div className="rounded-[24px] border border-sand bg-paper p-7 flex flex-col">
            <div className="mb-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-taupe mb-3">Ancrage</p>
              <div className="flex items-end gap-2 mb-1">
                <span className="font-serif text-[40px] font-semibold text-ink leading-none">19 €</span>
              </div>
              <p className="text-[12px] text-taupe mt-1">paiement unique · accès 21 jours</p>
            </div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {[
                '2 bilans par jour (matin + soir)',
                'Score et profil nerveux',
                '1 exercice de régulation adapté',
                'Historique 21 jours',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-taupe flex-shrink-0 mt-0.5" />
                  <span className="text-[13px] text-taupe leading-snug">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCheckout('ancrage')}
              disabled={paymentLoading === 'ancrage'}
              className="w-full border border-ink/20 text-ink hover:bg-ink hover:text-paper font-medium px-5 py-3 rounded-xl text-[13.5px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paymentLoading === 'ancrage' ? 'Chargement…' : 'Commencer Ancrage →'}
            </button>
            <p className="text-[11px] text-taupe/50 mt-2 text-center">Paiement unique · 19 €</p>
            <p className="text-xs text-stone/50 text-center mt-2">
              En procédant au paiement, vous acceptez nos{' '}
              <a href="/fr/conditions-utilisation" className="underline">CGU</a>{' '}
              et notre{' '}
              <a href="/fr/confidentialite" className="underline">Politique de confidentialité</a>.
            </p>
          </div>

          {/* ── Carte Intégration ── */}
          <div className="rounded-[24px] border border-lapis/30 bg-paper p-7 flex flex-col">
            <div className="mb-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-lapis mb-3">Intégration</p>
              <div className="flex items-end gap-2 mb-1">
                <span className="font-serif text-[40px] font-semibold text-ink leading-none">69 €</span>
              </div>
              <p className="text-[12px] text-taupe mt-1">paiement unique · accès 6 mois</p>
            </div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {[
                'Tout Ancrage',
                'Protocoles de régulation avancés',
                'Rapport hebdomadaire personnalisé',
                'Export PDF avancé',
                'Historique 6 mois',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-lapis flex-shrink-0 mt-0.5" />
                  <span className="text-[13px] text-ink/75 leading-snug">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCheckout('integration')}
              disabled={paymentLoading === 'integration'}
              className="w-full border border-lapis text-lapis hover:bg-lapis hover:text-white font-semibold px-5 py-3 rounded-xl text-[13.5px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paymentLoading === 'integration' ? 'Chargement…' : 'Commencer Intégration →'}
            </button>
            <p className="text-[11px] text-taupe/50 mt-2 text-center">Paiement unique · 69 €</p>
            <p className="text-xs text-stone/50 text-center mt-2">
              En procédant au paiement, vous acceptez nos{' '}
              <a href="/fr/conditions-utilisation" className="underline">CGU</a>{' '}
              et notre{' '}
              <a href="/fr/confidentialite" className="underline">Politique de confidentialité</a>.
            </p>
          </div>

          {/* ── Carte Praticien — mise en avant ── */}
          <div className="rounded-[24px] bg-ink p-7 flex flex-col relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle at 70% 20%, #6B9FD4 0%, transparent 60%)' }}
            />
            <div className="relative mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-amber">Praticien</p>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber/15 text-amber px-2 py-1 rounded-full leading-none">
                  Meilleur accès
                </span>
              </div>
              <div className="flex items-end gap-2 mb-1">
                <span className="font-serif text-[40px] font-semibold text-paper leading-none">89 €</span>
              </div>
              <p className="text-[12px] text-paper/50 mt-1">paiement unique · accès 6 mois</p>
            </div>
            <ul className="space-y-2.5 mb-8 flex-1 relative">
              {[
                'Tout Intégration',
                'Protocoles avancés complets',
                'Accompagnement communauté',
                'Accès bêta aux nouvelles fonctions',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber flex-shrink-0 mt-0.5" />
                  <span className="text-[13px] text-paper/75 leading-snug">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCheckout('praticien')}
              disabled={paymentLoading === 'praticien'}
              className="relative w-full bg-lapis hover:bg-lapis-deep text-white font-semibold px-5 py-3 rounded-xl text-[13.5px] transition-all duration-200 hover:-translate-y-px hover:shadow-[0_8px_40px_rgba(43,95,142,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paymentLoading === 'praticien' ? 'Chargement…' : 'Commencer Praticien →'}
            </button>
            <p className="text-[11px] text-paper/30 mt-2 text-center relative">
              Paiement sécurisé · accès 6 mois
            </p>
            <p className="text-xs text-paper/30 text-center mt-2 relative">
              En procédant au paiement, vous acceptez nos{' '}
              <a href="/fr/conditions-utilisation" className="underline">CGU</a>{' '}
              et notre{' '}
              <a href="/fr/confidentialite" className="underline">Politique de confidentialité</a>.
            </p>
          </div>

        </div>
      </section>

      {/* ── Garanties ── */}
      <section className="px-5 md:px-8 pb-14 max-w-[760px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: <Shield className="w-5 h-5 text-lapis" />,
              title: 'Paiement sécurisé',
              desc: 'Mollie · chiffrement TLS · aucune donnée bancaire stockée.',
            },
            {
              icon: <Clock className="w-5 h-5 text-amber" />,
              title: 'Paiement unique',
              desc: "Aucun renouvellement automatique. Vous payez une fois, c'est tout.",
            },
            {
              icon: <Zap className="w-5 h-5 text-ventral" />,
              title: 'Accès immédiat',
              desc: 'Votre accès est actif dans les secondes qui suivent le paiement.',
            },
          ].map((g, i) => (
            <div key={i} className="bg-sand/20 rounded-[16px] p-5 flex gap-3">
              <div className="flex-shrink-0 mt-0.5">{g.icon}</div>
              <div>
                <p className="text-[13px] font-semibold text-ink mb-1">{g.title}</p>
                <p className="text-[12px] text-taupe leading-relaxed">{g.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-5 md:px-8 pb-20 max-w-[760px] mx-auto">
        <h2 className="font-serif text-[26px] font-semibold text-ink mb-8 text-center">
          Questions fréquentes
        </h2>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <div
              key={i}
              className="bg-paper border border-[rgba(28,27,24,0.08)] rounded-[14px] overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 focus:outline-none"
              >
                <span className="text-[14px] font-medium text-ink">{item.q}</span>
                <span
                  className={`text-taupe text-[18px] leading-none flex-shrink-0 transition-transform duration-200 ${
                    openFaq === i ? 'rotate-45' : ''
                  }`}
                >
                  +
                </span>
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4 border-t border-[rgba(28,27,24,0.05)]">
                  <p className="text-[14px] text-taupe leading-relaxed pt-3">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA final ── */}
      <section className="px-5 md:px-8 pb-24 max-w-[760px] mx-auto text-center">
        <div className="bg-ink rounded-[24px] p-10 md:p-12">
          <Star className="w-8 h-8 text-amber mx-auto mb-4" />
          <h2 className="font-serif text-[26px] md:text-[32px] font-semibold text-paper mb-3 leading-tight">
            Ton système nerveux n'attend pas.
          </h2>
          <p className="text-[15px] text-paper/60 mb-8 max-w-[380px] mx-auto leading-relaxed">
            Commence maintenant — votre première séance est gratuite, sans inscription, en moins d'une minute.
          </p>
          <AppLink
            to="/fr/evaluation"
            className="inline-flex items-center gap-2 bg-lapis hover:bg-lapis-deep text-white font-semibold px-8 py-4 rounded-xl text-[15px] transition-all hover:-translate-y-px hover:shadow-[0_8px_40px_rgba(43,95,142,0.22)]"
          >
            Faire mon évaluation gratuite
            <ArrowRight className="w-4 h-4" />
          </AppLink>
        </div>
      </section>

      <BlogFooter />
    </div>
  );
}
